import { useCallback, useEffect, useState } from "react";
import { MdHistory } from "react-icons/md";
import AjudaTela from "../../components/AjudaTela";
import Layout from "../../components/Layout";
import { api } from "../../services/api";

const AJUDA = {
  titulo: "Consulta de Auditoria",
  descricao:
    "Histórico de operações sensíveis do sistema (estornos, exclusões, alterações de conta já paga, alterações de configuração, autorizações e login). É só leitura — não existe edição ou exclusão de um registro de auditoria por ninguém, nem administrador.",
  secoes: [
    {
      titulo: "Filtros",
      campos: [
        { nome: "Usuário", obrigatorio: false, explicacao: "Quem realizou a operação." },
        { nome: "Módulo", obrigatorio: false, explicacao: "Área do sistema onde a operação ocorreu (financeiro, almoxarifado, configurações, etc.)." },
        { nome: "Tipo de operação", obrigatorio: false, explicacao: "Criação, alteração, exclusão, estorno, autorização, etc." },
        { nome: "Período", obrigatorio: false, explicacao: "Intervalo de datas em que a operação ocorreu." },
      ],
    },
  ],
  dicas: [
    "\"Autorizado por\" só aparece quando a operação exigiu aprovação de um supervisor.",
    "\"Valor anterior\"/\"Valor novo\" mostram só os campos relevantes da operação, não o registro inteiro.",
  ],
};

const TIPOS_OPERACAO = [
  { value: "", label: "Todos" },
  { value: "criacao", label: "Criação" },
  { value: "alteracao", label: "Alteração" },
  { value: "exclusao", label: "Exclusão" },
  { value: "estorno", label: "Estorno" },
  { value: "ajuste_estoque", label: "Ajuste de Estoque" },
  { value: "autorizacao", label: "Autorização" },
  { value: "configuracao", label: "Alteração de Configuração" },
  { value: "login", label: "Login" },
];

const MODULOS = [
  { value: "", label: "Todos" },
  { value: "financeiro", label: "Financeiro" },
  { value: "almoxarifado", label: "Almoxarifado" },
  { value: "agricultura", label: "Agricultura" },
  { value: "pecuaria", label: "Pecuária" },
  { value: "configuracoes", label: "Configurações" },
  { value: "auth", label: "Autenticação" },
];

export default function ConsultaAuditoria() {
  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [colaboradores, setColaboradores] = useState([]);
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);

  const [filtros, setFiltros] = useState({
    usuario: "",
    modulo: "",
    tipo_operacao: "",
    data_inicio: "",
    data_fim: "",
    objeto_id: "",
  });

  const carregarColaboradores = useCallback(async () => {
    try {
      const resp = await api.get("/configuracoes/perfis-acesso/");
      setColaboradores(resp.data?.results || resp.data || []);
    } catch (error) {
      console.error("Erro ao carregar colaboradores:", error);
    }
  }, []);

  const carregarRegistros = useCallback(async () => {
    try {
      setCarregando(true);
      const params = new URLSearchParams();
      if (filtros.usuario) params.append("usuario", filtros.usuario);
      if (filtros.modulo) params.append("modulo", filtros.modulo);
      if (filtros.tipo_operacao) params.append("tipo_operacao", filtros.tipo_operacao);
      if (filtros.data_inicio) params.append("data_inicio", filtros.data_inicio);
      if (filtros.data_fim) params.append("data_fim", filtros.data_fim);
      if (filtros.objeto_id) params.append("objeto_id", filtros.objeto_id);
      params.append("page", pagina);

      const resp = await api.get(`/auditoria/registros/?${params}`);
      const dados = resp.data?.results || resp.data || [];
      setRegistros(Array.isArray(dados) ? dados : []);
      setTotalRegistros(resp.data?.count ?? dados.length);
      setTotalPaginas(Math.max(1, Math.ceil((resp.data?.count ?? dados.length) / 100)));
    } catch (error) {
      console.error("Erro ao carregar auditoria:", error);
      setErro("Erro ao carregar registros de auditoria.");
    } finally {
      setCarregando(false);
    }
  }, [filtros, pagina]);

  useEffect(() => {
    carregarColaboradores();
  }, [carregarColaboradores]);

  useEffect(() => {
    carregarRegistros();
  }, [carregarRegistros]);

  const aplicarFiltros = () => {
    setPagina(1);
  };

  const limparFiltros = () => {
    setFiltros({ usuario: "", modulo: "", tipo_operacao: "", data_inicio: "", data_fim: "", objeto_id: "" });
    setPagina(1);
  };

  const formatarDataHora = (valor) => (valor ? new Date(valor).toLocaleString("pt-BR") : "—");

  const formatarValor = (valor) => {
    if (valor === null || valor === undefined) return "—";
    if (typeof valor === "object") {
      return Object.entries(valor)
        .map(([campo, v]) => `${campo}: ${v}`)
        .join(", ");
    }
    return String(valor);
  };

  return (
    <Layout>
      <div className="p-6 font-medium max-w-7xl mx-auto">
        <div className="flex items-center gap-2 mb-8">
          <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            Consulta de Auditoria <MdHistory size={34} />
          </h2>
          <AjudaTela {...AJUDA} />
        </div>

        {erro && <div className="mb-4 p-3 rounded-lg bg-red-100 text-red-700 border border-red-200">{erro}</div>}

        <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Filtros de Busca</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Usuário</label>
              <select
                value={filtros.usuario}
                onChange={(e) => setFiltros({ ...filtros, usuario: e.target.value })}
                className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
              >
                <option value="">Todos</option>
                {colaboradores.map((c) => (
                  <option key={c.usuario} value={c.usuario}>{c.nome_completo || c.username}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Módulo</label>
              <select
                value={filtros.modulo}
                onChange={(e) => setFiltros({ ...filtros, modulo: e.target.value })}
                className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
              >
                {MODULOS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Tipo de Operação</label>
              <select
                value={filtros.tipo_operacao}
                onChange={(e) => setFiltros({ ...filtros, tipo_operacao: e.target.value })}
                className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
              >
                {TIPOS_OPERACAO.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Data Início</label>
              <input
                type="date"
                value={filtros.data_inicio}
                onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })}
                className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Data Fim</label>
              <input
                type="date"
                value={filtros.data_fim}
                onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })}
                className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Registro (ID)</label>
              <input
                type="text"
                placeholder="ID do objeto afetado"
                value={filtros.objeto_id}
                onChange={(e) => setFiltros({ ...filtros, objeto_id: e.target.value })}
                className="border border-green-500 p-2 rounded focus:ring-2 focus:ring-green-300"
              />
            </div>
          </div>

          <div className="flex gap-3 mt-4">
            <button onClick={aplicarFiltros} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors">
              Aplicar Filtros
            </button>
            <button onClick={limparFiltros} className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded transition-colors">
              Limpar Filtros
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center flex-wrap gap-2">
            <p className="text-gray-600">{totalRegistros} registro(s) encontrado(s)</p>
            <div className="text-sm text-gray-500">Página {pagina} de {totalPaginas}</div>
          </div>

          {carregando ? (
            <div className="p-8 text-center text-gray-500">Carregando...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Data/Hora</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Usuário</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Módulo</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Operação</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Registro</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Descrição</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Valor anterior → novo</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Justificativa</th>
                    <th className="p-3 text-left text-sm font-semibold text-gray-700">Autorizado por</th>
                  </tr>
                </thead>
                <tbody>
                  {registros.map((r) => (
                    <tr key={r.id} className="border-t border-gray-200 hover:bg-gray-50 transition-colors">
                      <td className="p-3 text-sm whitespace-nowrap">{formatarDataHora(r.criado_em)}</td>
                      <td className="p-3 text-sm">{r.usuario_nome || "—"}</td>
                      <td className="p-3 text-sm">{r.modulo}</td>
                      <td className="p-3 text-sm">
                        <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                          {r.tipo_operacao_display}
                        </span>
                      </td>
                      <td className="p-3 text-sm">{r.modelo ? `${r.modelo} #${r.objeto_id ?? ""}` : "—"}</td>
                      <td className="p-3 text-sm max-w-xs truncate">{r.descricao || "—"}</td>
                      <td className="p-3 text-sm max-w-xs truncate">
                        {r.valor_anterior || r.valor_novo
                          ? `${formatarValor(r.valor_anterior)} → ${formatarValor(r.valor_novo)}`
                          : "—"}
                      </td>
                      <td className="p-3 text-sm max-w-xs truncate">{r.justificativa || "—"}</td>
                      <td className="p-3 text-sm">{r.autorizado_por_nome || "—"}</td>
                    </tr>
                  ))}

                  {registros.length === 0 && (
                    <tr>
                      <td colSpan="9" className="p-8 text-center text-gray-500">
                        Nenhum registro de auditoria encontrado com os filtros selecionados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {totalPaginas > 1 && (
            <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-center items-center gap-2">
              <button
                onClick={() => setPagina((p) => Math.max(p - 1, 1))}
                disabled={pagina === 1}
                className={`px-3 py-1 rounded transition-colors ${pagina === 1 ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
              >
                ◀ Anterior
              </button>
              <span className="text-sm text-gray-600">Página {pagina} de {totalPaginas}</span>
              <button
                onClick={() => setPagina((p) => Math.min(p + 1, totalPaginas))}
                disabled={pagina === totalPaginas}
                className={`px-3 py-1 rounded transition-colors ${pagina === totalPaginas ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-blue-600 text-white hover:bg-blue-700"}`}
              >
                Próxima ▶
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
